import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/entities/Auth/store';
import { createEditHistoryStore } from '@/entities/AppChat/model/editHistoryStore';
import { useCanAccessPermission } from '@/shared/model/adminAccess';
import { Modal } from '@/shared/ui/Modal/Modal';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { MessageText } from '@/shared/ui/MessageText/MessageText';
import { RequestEmpty, RequestError, RequestLoading } from '@/shared/ui/RequestState';
import classes from './ChatMessageHistory.module.scss';

interface HistoryProps {
    dialogId: string;
    messageId: string;
    ownerAppUserId?: string;
}

/** Показывает исходный текст и сохранённые правки без изменения прочтения */
const HistoryDialog = (
    { dialogId, messageId, ownerAppUserId, onClose }: HistoryProps & { onClose: () => void }
) => {
    const store = useMemo(() => createEditHistoryStore(dialogId, messageId, ownerAppUserId), [dialogId, messageId, ownerAppUserId]);
    const state = store();

    useEffect(() => {
        void store.getState().load();

        return () => store.getState().reset();
    }, [store]);

    return <Modal
        visible
        title="История редактирования"
        size="sm"
        onHide={onClose}
    >
        <div className={classes.versions}>
            {state.items.map((edit) => <article key={edit.version}>
                <header>
                    <strong>Версия {edit.version}</strong>
                    <span>{edit.editorName || 'Пользователь VOX'}</span>
                    <time dateTime={edit.editedAt}>
                        {edit.editedAt && new Date(edit.editedAt).toLocaleString('ru-RU', {
                            timeZone: 'Europe/Moscow',
                            dateStyle: 'short',
                            timeStyle: 'short',
                        })}
                    </time>
                </header>
                <span>{edit.version === 1
                    ? 'Исходное сообщение'
                    : 'До правки'}</span>
                <p><MessageText text={edit.previousText || 'Без подписи к фотографиям'} /></p>
                <span>После правки</span>
                <p><MessageText text={edit.text || 'Без подписи к фотографиям'} /></p>
            </article>)}
            {state.loading &&
                <RequestLoading message="Загружаем правки…" />
            }
            {state.error &&
                <>
                    <RequestError
                        compact
                        message={state.error}
                    />
                    <ActionButton
                        label="Повторить"
                        onClick={() => void state.load()}
                    />
                </>
            }
            {state.loaded && !state.loading && !state.items.length &&
                <RequestEmpty message="История правок отсутствует" />
            }
            {state.hasMore && !state.error &&
                <ActionButton
                    label="Предыдущие версии"
                    color="secondary"
                    loading={state.loading}
                    disabled={state.loading}
                    onClick={() => void state.load()}
                />
            }
        </div>
    </Modal>;
};

/** Открывает историю отредактированного сообщения сотруднику с правом просмотра */
export const ChatMessageHistoryButton = (props: HistoryProps) => {
    const [open, setOpen] = useState(false);
    const sessionRevision = useAuth((state) => state.sessionRevision);
    const canView = useCanAccessPermission('admin.chats.view');

    useEffect(() => setOpen(false), [sessionRevision, props.dialogId, props.messageId, props.ownerAppUserId]);

    if (!canView) return <span className={classes.mark}>изменено</span>;

    return <>
        <button
            type="button"
            className={classes.trigger}
            onClick={() => setOpen(true)}
            aria-label="История редактирования"
            title="История редактирования"
        >
            изменено
        </button>
        {open &&
            <HistoryDialog
                {...props}
                onClose={() => setOpen(false)}
            />
        }
    </>;
};
